import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Shield, Award, FileCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { qualityContent } from '../../data/mock';

const QualityPage = () => {
  const stepIcons = [FileCheck, Shield, Award, CheckCircle2];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-[#242726]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="caption text-[#c4291c] mb-4 block">Excellence</span>
              <h1 className="heading-1 text-white mb-6">
                {qualityContent.title}
              </h1>
              <p className="body-large text-white/70 mb-6">
                {qualityContent.subtitle}
              </p>
              <p className="body-medium text-white/60">
                {qualityContent.description}
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                  src={qualityContent.image}
                  alt="Contrôle qualité PREBATI"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Steps */}
      <section className="section-padding bg-[#1a1c1b]">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="caption text-[#c4291c] mb-4 block">Processus qualité</span>
            <h2 className="heading-1 text-white mb-6">
              Contrôle à chaque étape
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {qualityContent.steps.map((step, index) => {
              const Icon = stepIcons[index];
              return (
                <div
                  key={index}
                  className="bg-[#2a2d2c] rounded-2xl p-8 border border-white/10 card-hover relative"
                >
                  <div className="absolute top-6 right-6 text-4xl font-bold text-white/5">
                    {String(index + 1).padStart(2, '0')}
                  </div>
                  <div className="w-14 h-14 rounded-2xl bg-[#c4291c]/10 flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-[#c4291c]" />
                  </div>
                  <h3 className="heading-3 text-white mb-3">{step.title}</h3>
                  <p className="text-white/60 body-medium">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="section-padding bg-[#242726]">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="caption text-[#c4291c] mb-4 block">Conformité</span>
            <h2 className="heading-2 text-white mb-6">
              Nos certifications et engagements
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {qualityContent.certifications.map((cert, index) => (
              <div
                key={index}
                className="bg-[#1a1c1b] rounded-xl p-6 border border-white/10 text-center"
              >
                <div className="w-12 h-12 rounded-xl bg-[#c4291c]/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-6 h-6 text-[#c4291c]" />
                </div>
                <p className="text-white font-medium">{cert}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Traceability */}
      <section className="section-padding bg-[#1a1c1b]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="bg-[#2a2d2c] rounded-2xl p-10 border border-white/10">
              <div className="grid md:grid-cols-2 gap-10 items-center">
                <div>
                  <h3 className="heading-2 text-white mb-6">
                    Traçabilité complète
                  </h3>
                  <p className="text-white/70 body-large mb-6">
                    Chaque lot de production est enregistré et traçable. Vous pouvez être sûrs de l'origine et de la qualité de chaque élément livré sur votre chantier.
                  </p>
                  <ul className="space-y-3">
                    {[
                      'Identification unique de chaque lot',
                      'Historique des contrôles',
                      'Certificats de conformité',
                      'Documentation technique complète'
                    ].map((item, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#c4291c]" />
                        <span className="text-white/70 body-medium">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="hidden md:flex items-center justify-center">
                  <div className="w-48 h-48 rounded-full border-4 border-[#c4291c]/20 flex items-center justify-center">
                    <div className="w-32 h-32 rounded-full border-4 border-[#c4291c]/40 flex items-center justify-center">
                      <Shield className="w-16 h-16 text-[#c4291c]" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#242726] border-t border-white/10">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="heading-3 text-white mb-2">
                La qualité au service de vos projets
              </h3>
              <p className="text-white/60 body-medium">
                Découvrez nos produits certifiés.
              </p>
            </div>
            <div className="flex gap-4 flex-wrap">
              <Link to="/products">
                <Button className="btn-secondary">
                  Nos produits
                </Button>
              </Link>
              <a href="https://wa.me/22243111616?text=" target="_blank" rel="noopener noreferrer">
                <Button className="btn-primary">
                  Demander un devis
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QualityPage;
